import { DropdownMenu } from 'radix-ui';
import { useTranslation } from 'react-i18next';

import { DROPDOWN_MENU_ITEM_CLASS as ITEM_CLASS } from '../components/menuStyles.ts';
import {
  DATE_ORDERS,
  DATE_SEPARATORS,
  HOUR_CYCLES,
  isDateOrder,
  isDateSeparator,
  isHourCycle,
} from './dateTimePreference.ts';
import { useDateTimeFormat } from './dateTimeFormatContext.ts';

/** Translation-key lookups — `web/src/locales/{en,es}/dateFormat.json` holds the actual strings.
 * The example dates embedded in the separator labels (`08/18/2026`) are digits, not translated. */
const DATE_ORDER_LABEL_KEY: Record<(typeof DATE_ORDERS)[number], string> = {
  MDY: 'dateOrder.MDY',
  DMY: 'dateOrder.DMY',
  YMD: 'dateOrder.YMD',
};

const SEPARATOR_LABEL_KEY: Record<(typeof DATE_SEPARATORS)[number], string> = {
  '/': 'separator.slash',
  '-': 'separator.dash',
  '.': 'separator.dot',
};

const HOUR_CYCLE_LABEL_KEY: Record<(typeof HOUR_CYCLES)[number], string> = {
  h12: 'time.h12',
  h24: 'time.h24',
};

/**
 * Per-user date/time display format — order, separator, and 12- vs. 24-hour time.
 * `claude-kit/rules/invariants.md`'s "Every displayed date or time gets a user-configurable format",
 * and `dateTimePreference.ts`'s own header on why this is the first place it applies. Radix
 * `DropdownMenu`, matching `theme/ThemeMenu.tsx` exactly — same roving-focus/dismissal reasoning, and
 * `asChild` avoided for the same recorded hazard.
 */
export function DateTimeFormatMenu() {
  const { t } = useTranslation('dateFormat');
  const { preference, setDateOrder, setSeparator, setHourCycle } = useDateTimeFormat();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className="inline-flex items-center gap-2 rounded-control border border-line bg-surface px-3 py-2 text-sm font-medium text-ink hover:bg-raised"
        aria-label={t('ariaLabel', {
          dateOrder: t(DATE_ORDER_LABEL_KEY[preference.dateOrder]),
          hourCycle: t(HOUR_CYCLE_LABEL_KEY[preference.hourCycle]),
        })}
      >
        {t('trigger')}
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={6}
          align="end"
          className="z-50 min-w-64 rounded-panel border border-line bg-surface p-1 text-ink shadow-overlay"
        >
          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">
            {t('dateOrder.groupLabel')}
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup
            value={preference.dateOrder}
            onValueChange={(value) => {
              if (isDateOrder(value)) setDateOrder(value);
            }}
          >
            {DATE_ORDERS.map((order) => (
              <DropdownMenu.RadioItem key={order} value={order} className={ITEM_CLASS}>
                <span aria-hidden="true" className="w-4 text-accent-text">
                  <DropdownMenu.ItemIndicator>✓</DropdownMenu.ItemIndicator>
                </span>
                {t(DATE_ORDER_LABEL_KEY[order])}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>

          <DropdownMenu.Separator className="my-1 h-px bg-line" />

          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">
            {t('separator.groupLabel')}
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup
            value={preference.separator}
            onValueChange={(value) => {
              if (isDateSeparator(value)) setSeparator(value);
            }}
          >
            {DATE_SEPARATORS.map((separator) => (
              <DropdownMenu.RadioItem key={separator} value={separator} className={ITEM_CLASS}>
                <span aria-hidden="true" className="w-4 text-accent-text">
                  <DropdownMenu.ItemIndicator>✓</DropdownMenu.ItemIndicator>
                </span>
                {t(SEPARATOR_LABEL_KEY[separator])}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>

          <DropdownMenu.Separator className="my-1 h-px bg-line" />

          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">
            {t('time.groupLabel')}
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup
            value={preference.hourCycle}
            onValueChange={(value) => {
              if (isHourCycle(value)) setHourCycle(value);
            }}
          >
            {HOUR_CYCLES.map((cycle) => (
              <DropdownMenu.RadioItem key={cycle} value={cycle} className={ITEM_CLASS}>
                <span aria-hidden="true" className="w-4 text-accent-text">
                  <DropdownMenu.ItemIndicator>✓</DropdownMenu.ItemIndicator>
                </span>
                {t(HOUR_CYCLE_LABEL_KEY[cycle])}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
